import { useQuery } from '@tanstack/react-query';
import { useDataProvider } from 'react-admin';
import { useRegion } from '../auth/RegionContext';
import { buildAttributionView, type AttributionView } from './attributionModel';
import type { Click, Merchant, Transaction, User } from '../dataProvider/types';

/**
 * Region-driven Manual attribution view, read through the aggregation seam
 * (`buildAttributionView`), NOT computed in the component. For v1 it pulls the raw
 * transactions, clicks, users and merchants from the mock provider and aggregates
 * client-side; the real-data migration replaces the fetch-and-aggregate here with a
 * single server-side endpoint and the surface does not change. Same seam idiom as
 * useReferrals.
 *
 * Transactions give the orphans (userId null) and their merchant-derived market;
 * clicks are the candidate match set; users resolve member names; merchants resolve
 * store names. Scope comes from the region context (UK | US | GLOBAL): the surface
 * shows every band the scope derives to at once, so it keys off `region`.
 */

const ALL = {
  pagination: { page: 1, perPage: 100_000 },
  sort: { field: 'id', order: 'ASC' as const },
  filter: {},
};

export const useAttribution = (): {
  view: AttributionView | null;
  isLoading: boolean;
} => {
  const dataProvider = useDataProvider();
  const { region } = useRegion();

  const { data, isLoading } = useQuery({
    queryKey: ['attribution', region],
    queryFn: async () => {
      const [transactions, clicks, users, merchants] = await Promise.all([
        dataProvider.getList('transactions', ALL),
        dataProvider.getList('clicks', ALL),
        dataProvider.getList('users', ALL),
        dataProvider.getList('merchants', ALL),
      ]);
      return buildAttributionView(
        {
          transactions: transactions.data as unknown as Transaction[],
          clicks: clicks.data as unknown as Click[],
          users: users.data as unknown as User[],
          merchants: merchants.data as unknown as Merchant[],
        },
        { scope: region },
      );
    },
  });

  return { view: data ?? null, isLoading };
};
